import { Component } from '@theme/component';

/**
 * @typedef {Object} StickySelectSizeRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} selectSizeButton - The bar's button
 */

const SLIDE_OFFSET = 'calc(100% + 24px)';

/**
 * A sticky "Select size" bar that takes over when the in-flow add-to-cart
 * scrolls out of the viewport top and follows the entire page. Its button
 * scrolls back to the variant picker and opens the size selector (anchored
 * dropdown on desktop, bottom-sheet on mobile).
 *
 * Show/hide slides are driven via the Web Animations API: the theme's
 * reduced-animations setting zeroes out ALL CSS transitions globally, so a
 * CSS-only slide would pop. WAAPI bypasses that while still honouring the
 * OS-level prefers-reduced-motion.
 *
 * @extends {Component<StickySelectSizeRefs>}
 */
class StickySelectSizeComponent extends Component {
  requiredRefs = ['stickyBar', 'selectSizeButton'];

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {number | null} */
  #scrollFrame = null;

  /** @type {AbortController | null} */
  #scrollInterrupt = null;

  /** @type {Animation | null} */
  #slideAnimation = null;

  /** @type {boolean} */
  #isStuck = false;

  connectedCallback() {
    super.connectedCallback();
    this.#setupObserver();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#slideAnimation?.cancel();
    this.#cancelScroll();
  }

  /**
   * Called after a section morph. A full-page morph (combined listings)
   * resets data-stuck to the server-rendered value and may replace the
   * observed element — re-assert both from component state.
   */
  updatedCallback() {
    super.updatedCallback();
    this.refs.stickyBar.dataset.stuck = this.#isStuck ? 'true' : 'false';
    this.#setupObserver();
  }

  #setupObserver() {
    this.#observer?.disconnect();

    const section = this.closest('.shopify-section');
    if (!section) return;
    // Scope to this product's own buy buttons - the section can hold more
    // than one product form. The sticky mobile wrapper (when enabled) is the
    // element the customer actually sees as "the add to cart";
    // `.buy-buttons-block` itself has no box on mobile (display: contents),
    // so it can never intersect.
    const productForm = section.querySelector(
      `product-form-component[data-product-id="${this.dataset.productId}"]`
    );
    const buyButtonsBlock = productForm?.closest('.buy-buttons-block') ?? section.querySelector('.buy-buttons-block');
    if (!buyButtonsBlock) return;
    const target = buyButtonsBlock.querySelector('.sticky-atc-mobile') ?? buyButtonsBlock;

    this.#observer = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;
      // Show only once the in-flow add-to-cart has been scrolled past (fully
      // above the viewport) — not while it is still below, unreached.
      this.#applyStuck(!entry.isIntersecting && entry.boundingClientRect.bottom <= 0);
    });
    this.#observer.observe(target);
  }

  /**
   * Transitions the bar in/out with a WAAPI slide.
   * @param {boolean} stuck
   */
  #applyStuck(stuck) {
    if (stuck === this.#isStuck) return;
    this.#isStuck = stuck;

    const bar = this.refs.stickyBar;
    this.#slideAnimation?.cancel();
    this.#slideAnimation = null;

    if (this.#prefersReducedMotion()) {
      bar.dataset.stuck = stuck ? 'true' : 'false';
      return;
    }

    if (stuck) {
      bar.dataset.stuck = 'true';
      this.#slideAnimation = bar.animate(
        [
          { transform: `translateX(-50%) translateY(${SLIDE_OFFSET})`, opacity: 0 },
          { transform: 'translateX(-50%) translateY(0)', opacity: 1 },
        ],
        { duration: 280, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' }
      );
    } else {
      // Keep data-stuck (and with it visibility) until the slide-out ends.
      this.#slideAnimation = bar.animate(
        [
          { transform: 'translateX(-50%) translateY(0)', opacity: 1 },
          { transform: `translateX(-50%) translateY(${SLIDE_OFFSET})`, opacity: 0 },
        ],
        { duration: 200, easing: 'cubic-bezier(0.55, 0, 1, 0.45)' }
      );
      this.#slideAnimation.finished.then(
        () => {
          bar.dataset.stuck = 'false';
        },
        () => {}
      );
    }
  }

  /**
   * Scrolls back to the variant picker, then opens the size selector.
   */
  handleSelectSizeClick = () => {
    const section = this.closest('.shopify-section');
    if (!section) return;

    const trigger = section.querySelector('variant-custom-dropdown .variant-custom-dropdown__trigger');
    const scrollTarget =
      trigger?.closest('variant-custom-dropdown') ??
      section.querySelector('variant-picker') ??
      section.querySelector('.product-details') ??
      section;

    const rect = scrollTarget.getBoundingClientRect();
    const destination = Math.max(0, window.scrollY + rect.top - (window.innerHeight - rect.height) / 2);

    const openTrigger = () => {
      if (!(trigger instanceof HTMLElement)) return;
      requestAnimationFrame(() => {
        const triggerRect = trigger.getBoundingClientRect();
        if (triggerRect.top >= 0 && triggerRect.bottom <= window.innerHeight) trigger.click();
      });
    };

    if (this.#prefersReducedMotion()) {
      window.scrollTo({ top: destination, behavior: 'instant' });
      openTrigger();
      return;
    }

    this.#animateScrollTo(destination).then((completed) => {
      if (completed) openTrigger();
    });
  };

  /**
   * Owned scroll animation — faster than native smooth scrolling and with a
   * known end, so the dropdown (which dismisses itself on any window scroll)
   * can be opened the moment the scroll is done. A wheel, touch, or key from
   * the customer cancels it and hands control back.
   * @param {number} destination
   * @returns {Promise<boolean>} true when the scroll ran to completion
   */
  #animateScrollTo(destination) {
    this.#cancelScroll();

    const start = window.scrollY;
    const distance = destination - start;
    if (Math.abs(distance) < 1) return Promise.resolve(true);

    const duration = Math.min(600, Math.max(300, Math.abs(distance) / 8));
    const startTime = performance.now();

    return new Promise((resolve) => {
      const interrupt = new AbortController();
      this.#scrollInterrupt = interrupt;
      const cancel = () => {
        if (this.#scrollFrame) cancelAnimationFrame(this.#scrollFrame);
        this.#scrollFrame = null;
        interrupt.abort();
        this.#scrollInterrupt = null;
        resolve(false);
      };
      for (const type of ['wheel', 'touchstart', 'mousedown', 'keydown']) {
        window.addEventListener(type, cancel, { signal: interrupt.signal, passive: true });
      }

      const step = () => {
        this.#scrollFrame = null;
        const progress = Math.min(1, (performance.now() - startTime) / duration);
        const eased = 1 - Math.pow(1 - progress, 3);
        window.scrollTo({ top: start + distance * eased, behavior: 'instant' });

        if (progress >= 1) {
          interrupt.abort();
          this.#scrollInterrupt = null;
          resolve(true);
          return;
        }
        this.#scrollFrame = requestAnimationFrame(step);
      };

      this.#scrollFrame = requestAnimationFrame(step);
    });
  }

  #cancelScroll() {
    if (this.#scrollFrame) cancelAnimationFrame(this.#scrollFrame);
    this.#scrollFrame = null;
    this.#scrollInterrupt?.abort();
    this.#scrollInterrupt = null;
  }

  #prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
}

if (!customElements.get('sticky-select-size')) {
  customElements.define('sticky-select-size', StickySelectSizeComponent);
}
